"use client"

import { createContext, useContext, useEffect, useRef, useState } from "react"
import { useSession } from "next-auth/react"

interface SSECounts {
  unreadCount: number
  activityCount: number
}

const SSEContext = createContext<SSECounts>({ unreadCount: 0, activityCount: 0 })

export function useSSECounts() {
  return useContext(SSEContext)
}

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * CountsProvider (formerly SSEProvider)
 *
 * Polls /api/notifications/counts every 5 minutes for badge counts.
 * Replaced the SSE approach which held a 60-second serverless function
 * open continuously for every logged-in user — very expensive on Vercel.
 *
 * Exported as SSEProvider for backwards compatibility with existing imports.
 */
export function SSEProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const [counts, setCounts] = useState<SSECounts>({ unreadCount: 0, activityCount: 0 })
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (status === "loading") return
    if (!session?.user) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      setCounts({ unreadCount: 0, activityCount: 0 })
      return
    }

    const fetchCounts = async () => {
      try {
        const res = await fetch("/api/notifications/counts")
        if (!res.ok) return
        const { unread, activity } = await res.json()
        setCounts({ unreadCount: unread ?? 0, activityCount: activity ?? 0 })
      } catch {
        // Silently fail — stale counts are fine
      }
    }

    // Fetch immediately on mount / session change
    fetchCounts()

    // Then poll every 5 minutes
    intervalRef.current = setInterval(fetchCounts, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [session, status])

  return <SSEContext.Provider value={counts}>{children}</SSEContext.Provider>
}
